<template>
  <modal-fullscreen
    v-if="visible"
    :visible="visible"
    :title="modalTitle"
    @cancel="closeModal"
    width="1000px"
    :destroy-on-close="true"
  >
    <div v-if="loading" class="sequence-state">
      <a-spin />
      <span>Loading sequence record...</span>
    </div>

    <template v-else-if="loadError || notFound">
      <div class="sequence-error">
        <a-alert type="error" show-icon>
          <template slot="message">
            {{ notFound ? 'Sequence record not found' : "Couldn't load sequence record" }}
          </template>
          <template slot="description">
            {{
              notFound
                ? 'This sequence record may have been deleted or is no longer available.'
                : 'Check your connection and try again.'
            }}
          </template>
        </a-alert>
      </div>

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="closeModal">Close</a-button>
          <a-button type="primary" @click="fetchSequence">Try again</a-button>
        </div>
      </template>
    </template>

    <template v-else-if="sequence">
      <sequence-review-step
        :visible.sync="visibleStepModal"
        :step="associateStep"
        :title="evidenceModalTitle"
        :show-actions="false"
      />

      <sequence-review-document
        :template_id="adminStep?.template?.id"
        :visible.sync="visibleDocumentModal"
        :associate_id="associate.id"
        @save="saveDocument"
      />

      <a-modal
        v-model="visibleApproveAllConfirm"
        title="Approve all steps?"
        :destroy-on-close="true"
        @ok="approveAllSteps(true)"
      >
        <p>This will clear declined decisions and their reasons.</p>
        <template slot="footer">
          <div class="flex justify-content-between">
            <a-button @click="visibleApproveAllConfirm = false">Keep current decisions</a-button>
            <a-button type="primary" @click="approveAllSteps(true)">Approve all steps</a-button>
          </div>
        </template>
      </a-modal>

      <div class="sequence-review-layout">
        <sequence-review-summary
          :sequence="sequence"
          :associate="associate"
          :is-review-mode="isReviewMode"
          :is-reviewed-view="isReviewedView"
          :associate-steps-completed="associateStepsCompleted"
          :associate-steps-total="associateStepsTotal"
          :admin-steps-completed="adminStepsCompleted"
          :admin-steps-total="targetAdminSteps.length"
          :export-payload="exportPayload"
        />

        <template v-if="isReviewMode">
          <a-steps :current="current" class="sequence-wizard">
            <a-step title="Check Associate" :status="stepStatus(0)" />
            <a-step title="Review Steps" :status="stepStatus(1)" />
            <a-step title="Complete Admin Tasks" :status="stepStatus(2)" />
            <a-step title="Sign Review" :status="stepStatus(3)" />
          </a-steps>

          <sequence-review-associate-check
            v-show="current === 0"
            :associate="associate"
            :checked="associateInfoChecked"
            :show-validation="showAssociateInfoValidation"
            @change="handleAssociateInfoCheck"
          />

          <sequence-review-associate-steps
            v-show="current === 1"
            :steps="associateSteps"
            :show-validation="showStepsValidation"
            @approve-all="handleApproveAll"
            @open-step="openStepModal"
            @approve-step="approveStep"
            @decline-step="declineStep"
            @update-decline-reason="updateDeclineReason"
          />

          <sequence-review-admin-tasks
            v-show="current === 2"
            :steps="targetAdminSteps"
            :can-manage-documents="hasPermission('manage_documents')"
            :show-validation="showAdminTasksValidation"
            @open-document-modal="openDocumentModal"
            @open-document-view="openDocumentView"
          />

          <sequence-review-signature
            v-show="current === 3"
            ref="signatureRef"
            :show-validation="showSignatureValidation"
            @checked="handleSignatureCheck"
            @save-signature="setSignatureId"
          />

          <div v-if="approveBlockReasons.length">
            <a-alert type="warning" show-icon>
              <template slot="message">Why actions are unavailable</template>
              <template slot="description">
                <ul class="m-0">
                  <li v-for="reason in approveBlockReasons" :key="reason">{{ reason }}</li>
                </ul>
              </template>
            </a-alert>
          </div>
        </template>

        <template v-else>
          <sequence-review-record
            :sequence="sequence"
            :associate-steps="associateSteps"
            :target-admin-steps="targetAdminSteps"
            :declined-steps="declinedSteps"
            @open-step="openStepModal"
            @open-document-view="openDocumentView"
            @scroll-to-declined="scrollToDeclinedSteps"
          />
        </template>
      </div>

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="closeModal">Close</a-button>
          <div class="flex justify-content-end gap-2" v-if="isReviewMode">
            <a-button v-if="current !== 0" type="primary" ghost @click="current = current - 1" class="m-0">
              Previous step
            </a-button>
            <a-button v-if="current !== 3" type="primary" class="m-0" :disabled="isNextStepDisabled" @click="goNext">
              Next step
            </a-button>
            <a-tooltip v-else>
              <template slot="title" v-if="!hasPermission('manage_documents')">
                You must have Manage Documents permission to check In-Service.
              </template>
              <a-button
                type="primary"
                class="m-0"
                @click="completeReview"
                :disabled="disabledReview || !hasPermission('manage_documents')"
                :loading="loadingReview"
              >
                Complete review
              </a-button>
            </a-tooltip>
          </div>
        </div>
      </template>
    </template>
  </modal-fullscreen>
</template>

<script>
import { Alert, Button, Modal, notification, Spin, Steps, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import SequenceReviewStep from './sequence-review-step.vue'
import ProfileCreateDocumentTemplate from '@/modules/profile/components/profile-create-document-template.vue'
import permissionMixin from '@/mixins/permissionMixin'
import { SEQUENCE_APPROVE_BLOCK_REASONS } from '@/store/modules/sequence/constants'
import { getEvidenceTitle, getTypeTitle, normalizeReviewAdminStep } from './sequence-review.helpers'
import { getTargetAdminSteps } from './sequence-review-scope'
import SequenceReviewAdminTasks from './sequence-review-admin-tasks.vue'
import SequenceReviewAssociateCheck from './sequence-review-associate-check.vue'
import SequenceReviewAssociateSteps from './sequence-review-associate-steps.vue'
import SequenceReviewRecord from './sequence-review-record.vue'
import SequenceReviewSignature from './sequence-review-signature.vue'
import SequenceReviewSummary from './sequence-review-summary.vue'

const { mapActions: sequenceActions, mapState: sequenceState } = createNamespacedHelpers('sequence')
const { mapActions: modalActions } = createNamespacedHelpers('modals')
const { mapActions: documentActions } = createNamespacedHelpers('document')

export default {
  components: {
    'a-alert': Alert,
    'a-steps': Steps,
    'a-step': Steps.Step,
    'a-button': Button,
    'a-modal': Modal,
    'a-tooltip': Tooltip,
    'a-spin': Spin,
    'sequence-review-step': SequenceReviewStep,
    'sequence-review-document': ProfileCreateDocumentTemplate,
    'sequence-review-admin-tasks': SequenceReviewAdminTasks,
    'sequence-review-associate-check': SequenceReviewAssociateCheck,
    'sequence-review-associate-steps': SequenceReviewAssociateSteps,
    'sequence-review-record': SequenceReviewRecord,
    'sequence-review-signature': SequenceReviewSignature,
    'sequence-review-summary': SequenceReviewSummary
  },
  mixins: [permissionMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associate: {
      type: Object,
      default: () => ({})
    },
    sequence_id: {
      type: Number,
      default: undefined
    },
    is_valid_id: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      current: 0,
      associateStep: undefined,
      adminStep: undefined,

      visibleStepModal: false,
      visibleDocumentModal: false,
      visibleApproveAllConfirm: false,

      associateInfoChecked: false,
      signatureConfirmed: false,
      signature_id: undefined,

      loading: false,
      loadingReview: false,
      loadError: null,
      notFound: false,

      showAssociateInfoValidation: false,
      showStepsValidation: false,
      showAdminTasksValidation: false,
      showSignatureValidation: false
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal && this.sequence_id && this.associate.id) {
          this.fetchSequence()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...sequenceState({
      sequence: state => state.completedSequence
    }),

    isReviewMode() {
      return Boolean(this.sequence && this.sequence.completion_status === 'completed')
    },

    isReviewedView() {
      return Boolean(this.sequence && !this.isReviewMode)
    },

    associateSteps() {
      return [...(this.sequence?.steps || [])].sort((a, b) => (a.number || 0) - (b.number || 0))
    },

    associateStepsTotal() {
      return this.associateSteps.length
    },

    associateStepsCompleted() {
      return this.associateSteps.filter(step => step.status === 'completed').length
    },

    adminStepsCompleted() {
      return this.targetAdminSteps.filter(step => step.document).length
    },

    allRequiredAssociateStepsReviewed() {
      return this.associateSteps
        .filter(step => step.is_required)
        .every(step => step.review?.status && (step.review.status !== 'declined' || Boolean(step.review.comment)))
    },

    hasDeclinedSteps() {
      return this.associateSteps.some(step => step.review?.status === 'declined')
    },

    declinedSteps() {
      return this.associateSteps.filter(step => step.review?.status === 'declined')
    },

    requiredAdminStepsCompleted() {
      return this.targetAdminSteps.filter(item => item.is_required).every(item => item.document)
    },

    targetAdminSteps() {
      return getTargetAdminSteps(this.sequence, this.associate)
    },

    disabledReview() {
      return !(
        this.associateInfoChecked &&
        this.allRequiredAssociateStepsReviewed &&
        this.requiredAdminStepsCompleted &&
        this.signatureConfirmed
      )
    },

    isNextStepDisabled() {
      if (this.current === 0) {
        return !this.associateInfoChecked
      }

      if (this.current === 1) {
        return !this.allRequiredAssociateStepsReviewed
      }

      if (this.current === 2) {
        return !this.requiredAdminStepsCompleted
      }

      return false
    },

    typeTitle() {
      return getTypeTitle(this.sequence?.type)
    },

    modalTitle() {
      if (!this.sequence) {
        return 'Sequence'
      }

      return this.isReviewMode ? `Review ${this.typeTitle}` : `${this.typeTitle}`
    },

    exportPayload() {
      return { type: 'sequence', object_ids: [this.sequence.id] }
    },

    evidenceModalTitle() {
      return getEvidenceTitle(this.associateStep)
    },

    approveBlockReasons() {
      if (!this.isReviewMode) {
        return []
      }

      const reasons = []

      if (!this.hasPermission('manage_documents')) {
        reasons.push(SEQUENCE_APPROVE_BLOCK_REASONS.MISSING_PERMISSION)
      }

      if (!this.allRequiredAssociateStepsReviewed && this.current === 1) {
        reasons.push(SEQUENCE_APPROVE_BLOCK_REASONS.STEPS_NOT_REVIEWED)
      }

      if (!this.associateInfoChecked && this.current === 0) {
        reasons.push('Check the associate information to continue.')
      }

      if (!this.requiredAdminStepsCompleted && this.current === 2) {
        reasons.push(SEQUENCE_APPROVE_BLOCK_REASONS.REQUIRED_ADMIN_DOCUMENTS)
      }

      if (!this.signatureConfirmed && this.current === 3) {
        reasons.push('Sign the review before completing it.')
      }

      return reasons
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...documentActions(['checkCreatedAssociateDocument']),

    ...sequenceActions(['getSequenceById', 'postDeclineSequence', 'postApproveSequence', 'getCompletedSequence']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    closeModal() {
      this.$emit('update:visible', false)
      this.resetLocalState()
    },

    resetLocalState() {
      this.current = 0
      this.associateInfoChecked = false
      this.signatureConfirmed = false
      this.signature_id = undefined
      this.loadError = null
      this.notFound = false
      this.showAssociateInfoValidation = false
      this.showStepsValidation = false
      this.showAdminTasksValidation = false
      this.showSignatureValidation = false
    },

    openStepModal(step) {
      this.visibleStepModal = true
      this.associateStep = step
    },

    openDocumentModal(step) {
      this.visibleDocumentModal = true
      this.adminStep = step
    },

    openDocumentView(initial_document) {
      this.openModal({
        type: 'attachedView',
        data: {
          visible: true,
          initial_document,
          onRenew: updatedDocument => {
            this.sequence.admin_steps = this.sequence.admin_steps.map(item => {
              return item.document?.id === updatedDocument.parent_id ? { ...item, document: updatedDocument } : item
            })
          },
          onUpdate: updatedDocument => {
            this.sequence.admin_steps = this.sequence.admin_steps.map(item => {
              return item.document?.id === updatedDocument.id ? { ...item, document: updatedDocument } : item
            })
          }
        }
      })
    },

    stepStatus(index) {
      if (this.current === index) {
        return 'process'
      }

      return this.current > index ? 'finish' : 'wait'
    },

    handleAssociateInfoCheck(event) {
      this.associateInfoChecked = event.target.checked

      if (this.associateInfoChecked) {
        this.showAssociateInfoValidation = false
      }
    },

    handleSignatureCheck(checked) {
      this.signatureConfirmed = checked

      if (this.signatureConfirmed) {
        this.showSignatureValidation = false
      }
    },

    updateStepReview(step, review) {
      this.$set(step, 'review', review)

      this.$nextTick(() => {
        if (this.allRequiredAssociateStepsReviewed) {
          this.showStepsValidation = false
        }
      })
    },

    approveStep(step) {
      this.updateStepReview(step, {
        comment: '',
        status: 'approved'
      })
    },

    declineStep(step) {
      this.updateStepReview(step, {
        comment: step.review?.comment || '',
        status: 'declined'
      })
    },

    updateDeclineReason(step, comment) {
      this.updateStepReview(step, {
        comment,
        status: 'declined'
      })
    },

    handleApproveAll() {
      if (this.hasDeclinedSteps) {
        this.visibleApproveAllConfirm = true
        return
      }

      this.approveAllSteps(false)
    },

    approveAllSteps(includeDeclined) {
      this.sequence.steps.forEach(step => {
        if (step.review?.status === 'declined' && !includeDeclined) {
          return
        }

        if (step.review?.status !== 'approved' || includeDeclined) {
          this.$set(step, 'review', {
            comment: '',
            status: 'approved'
          })
        }
      })
      this.visibleApproveAllConfirm = false

      this.$nextTick(() => {
        if (this.allRequiredAssociateStepsReviewed) {
          this.showStepsValidation = false
        }
      })
    },

    setSignatureId(signatureId) {
      this.signature_id = signatureId

      if (this.signature_id) {
        this.showSignatureValidation = false
      }
    },

    saveDocument(document) {
      this.$set(this.adminStep, 'document', document)

      this.$nextTick(() => {
        if (this.requiredAdminStepsCompleted) {
          this.showAdminTasksValidation = false
        }
      })
    },

    goNext() {
      if (this.current === 0 && !this.associateInfoChecked) {
        this.showAssociateInfoValidation = true
        return
      }

      if (this.current === 1 && !this.allRequiredAssociateStepsReviewed) {
        this.showStepsValidation = true
        return
      }

      if (this.current === 2 && !this.requiredAdminStepsCompleted) {
        this.showAdminTasksValidation = true
        return
      }

      if (this.current === 0) {
        this.showAssociateInfoValidation = false
      }

      if (this.current === 1) {
        this.showStepsValidation = false
      }

      if (this.current === 2) {
        this.showAdminTasksValidation = false
      }

      this.current += 1
    },

    async completeReview() {
      if (!this.signatureConfirmed) {
        this.showSignatureValidation = true
        return
      }

      this.showSignatureValidation = false

      await this.reviewSequence(this.hasDeclinedSteps ? 'decline' : 'approve')
    },

    async fetchSequence() {
      this.loading = true
      this.loadError = null
      this.notFound = false

      try {
        console.log(this.is_valid_id)
        if (this.is_valid_id) {
          const payload = {
            signed_sequence_id: this.sequence_id,
            associate_id: this.associate.id
          }

          await this.getSequenceById(payload)
        } else {
          const payload = {
            sequence_id: this.sequence_id,
            associate_id: this.associate.id
          }
          await this.getCompletedSequence(payload)
        }

        if (!this.sequence) {
          this.notFound = true
          return
        }

        const templateIdsToCheck = this.sequence.admin_steps
          .filter(step => !step.document && step.template?.id)
          .map(step => step.template.id)

        if (templateIdsToCheck.length === 0) {
          return
        }

        const templates = await this.checkCreatedAssociateDocument({
          associate_id: this.associate.id,
          ids: templateIdsToCheck
        })

        this.sequence.admin_steps = this.sequence.admin_steps.map(step => {
          if (step.document || !step.template?.id) {
            return step
          }

          const availableDocument = templates[step.template.id]

          return availableDocument ? { ...step, document: availableDocument } : step
        })
      } catch (error) {
        this.loadError = typeof error === 'string' ? error : error?.message
      } finally {
        this.loading = false
      }
    },

    async reviewSequence(type) {
      this.loadingReview = true
      try {
        await this.$refs.signatureRef.saveSignature()

        if (!this.sequence_id || !this.associate?.id || !this.signature_id) {
          this.showSignatureValidation = true
          return
        }

        const payload = {
          sequence_id: this.sequence_id,
          associate_id: this.associate?.id,
          signature_id: this.signature_id,
          project_sequence_id: this.sequence.project_sequence_id,
          signed_sequence_id: this.sequence.id,
          sequence: {
            ...this.sequence,
            admin_steps: this.sequence.admin_steps.map(normalizeReviewAdminStep)
          }
        }

        type === 'approve' ? await this.postApproveSequence(payload) : await this.postDeclineSequence(payload)

        this.showNotification('success', 'Success', 'Review completed.')

        this.$emit('save', type === 'approve' ? 'approved' : 'rejected')

        this.closeModal()
      } catch (error) {
        this.showNotification('error', 'Error', "Couldn't complete the review. Try again.")
      } finally {
        this.loadingReview = false
      }
    },

    scrollToDeclinedSteps() {
      this.$nextTick(() => {
        const declinedCard = this.$el.querySelector('.step-card--declined')

        if (declinedCard) {
          declinedCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  overflow-y: auto;
  max-height: 800px;
}

::v-deep .modal-fullscreen-wrap--expanded .ant-modal-body {
  max-height: none;
}

.sequence-state,
.sequence-error {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
}

.sequence-review-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sequence-wizard {
  padding: 0;
}
</style>
